import React from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import Line from '@components/common/line';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { WithColor } from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { useManageLinkCurrentNcFile } from './hooks/useManageLinkCurrentNcFile';
import LinkStatus from './components/LinkStatus';
import { LINK_STATUS } from '@helpers';
import type { ScaleFontProps, OnLinked } from '@components/search/operations/components/StartJobPopup/types';

export interface LinkCurrentNcFileProps extends WithColor, ScaleFontProps {
  productionOperationId?: number;
  onLinked?: OnLinked;
}

const LinkCurrentNcFile = ({ C, productionOperationId, onLinked, scale = initialScale, font = initialFont }: LinkCurrentNcFileProps) => {
  const { ncFileId, ncFileName, versionLabel, favoriteCaption, linkStatus, linkError, handleLink } = useManageLinkCurrentNcFile({
    productionOperationId,
    onLinked,
  });

  // Nothing is selected on the machine — no current NC file to show or link.
  if (!ncFileId) return null;

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });
  const isLinking = linkStatus === LINK_STATUS.Linking;
  const captionStyle = [font['Caption 1'], { color: C('Label/Light/Tertiary') }];

  // onlyLabel keeps the long action label on a single full-width row (Line otherwise reserves the value column).
  const linkRow = {
    name: 'Link NC Program Now On Machine',
    noArrow: true,
    showHoverEffect: true,
    onClick: isLinking ? undefined : handleLink,
  };

  return (
    <View style={{ width: '100%', marginTop: scale(18) }}>
      <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
        <Line
          {...{
            scale,
            font,
            item: linkRow,
            index: 0,
            array: [linkRow],
            C,
            smallSize: true,
            topBorder: false,
            roundTop: true,
            roundBottom: true,
            textColor: 'Link/Light/Primary',
            onlyLabel: true,
          }}
        />
      </View>
      <View style={{ marginTop: scale(3), paddingHorizontal: scale(15) }}>
        <LinkStatus status={linkStatus} error={linkError} C={C} font={font} />
        <Text style={captionStyle}>{i18n.t('NC program {{name}}', { name: ncFileName })}</Text>
        {!!versionLabel && <Text style={captionStyle}>{i18n.t('Version {{version}}', { version: versionLabel })}</Text>}
        {!!favoriteCaption && <Text style={captionStyle}>{favoriteCaption}</Text>}
      </View>
    </View>
  );
};

export default LinkCurrentNcFile;
